/** Captura de resultado set por set (maqueta aprobada por Eduardo
 * 2026-10-06): la usan el jugador («Reportar resultado») y el admin
 * («Editar» en la tabla de reportes). Lógica pura, sin React.
 *
 * NO hay reglas sacadas del formato del torneo (Eduardo quitó sets/súper
 * muerte del formato a propósito): solo se avisa lo imposible —una súper
 * muerte que no llega a 10 o no se gana por 2, un set incompleto o empatado,
 * el ganador marcado que no coincide con los sets, o 1-1 sin el set que
 * define—. El texto que se manda es el mismo de siempre (desde el jugador 1:
 * «6-4 7-6(7-4) TB 10-8», con « RET»/« DEF» al final). */

import { scoreDraftFromText, scoreTextFromDraft, setPairHasTiebreak, type ScoreSetDraft } from "./scoreDraft";

export type CaptureSetKind = "normal" | "super";
export type CaptureResultType = "normal" | "default" | "retiro";

export interface CaptureSet {
  kind: CaptureSetKind;
  /** Juegos (o puntos en súper muerte) del jugador 1 y del 2. */
  p1: string;
  p2: string;
  /** Puntos de tie-break de cada jugador (solo sets normales). */
  tb1: string;
  tb2: string;
}

export interface CaptureState {
  winner: number | null;
  resultType: CaptureResultType;
  sets: CaptureSet[];
  notes: string;
}

export interface CapturePlayer {
  id: number;
  name: string;
}

export const MIN_CAPTURE_SETS = 1;
export const MAX_CAPTURE_SETS = 5;
export const MAX_PICKLEBALL_GAMES = 3;

export function emptyCaptureSet(kind: CaptureSetKind = "normal"): CaptureSet {
  return { kind, p1: "", p2: "", tb1: "", tb2: "" };
}

export function maxSets(pickleball: boolean): number {
  return pickleball ? MAX_PICKLEBALL_GAMES : MAX_CAPTURE_SETS;
}

function num(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/** Lado que ganó el set (1 o 2), o null si falta un número o quedó empatado. */
export function setWinnerSide(set: CaptureSet): 1 | 2 | null {
  const a = num(set.p1);
  const b = num(set.p2);
  if (a === null || b === null || a === b) return null;
  return a > b ? 1 : 2;
}

/** ¿El set lleva renglón de tie-break? Solo 7-6/6-7 (o si ya trae puntos). */
export function tiebreakApplies(set: CaptureSet, pickleball: boolean): boolean {
  if (pickleball || set.kind === "super") return false;
  if (set.tb1.trim() || set.tb2.trim()) return true;
  return setPairHasTiebreak(set.p1.trim(), set.p2.trim());
}

export function setsTally(sets: CaptureSet[]): { p1: number; p2: number } {
  let p1 = 0;
  let p2 = 0;
  for (const set of sets) {
    const side = setWinnerSide(set);
    if (side === 1) p1 += 1;
    if (side === 2) p2 += 1;
  }
  return { p1, p2 };
}

/** Quién ganó según los sets (1, 2 o null si van empatados o no hay sets). */
export function impliedWinnerSide(sets: CaptureSet[]): 1 | 2 | null {
  const { p1, p2 } = setsTally(sets);
  if (p1 === p2) return null;
  return p1 > p2 ? 1 : 2;
}

/** Estado inicial desde un marcador guardado (o vacío: dos sets normales). */
export function captureFromScoreText(
  scoreText: string | null | undefined,
  winner: number | null | undefined,
  pickleball: boolean,
  notes = "",
): CaptureState {
  const text = scoreText ?? "";
  const resultType: CaptureResultType = /\bDEF\b/i.test(text) ? "default" : /\bRET\b/i.test(text) ? "retiro" : "normal";
  const drafts = scoreDraftFromText(text, pickleball).sets.filter((set, index) => index < 2 || set.p1 || set.p2);
  const sets = drafts.map((draft): CaptureSet => {
    const kind: CaptureSetKind = !pickleball && draft.superTb ? "super" : "normal";
    let tb1 = "";
    let tb2 = "";
    if (!pickleball && kind === "normal" && (draft.tbWin || draft.tbLose)) {
      const side = setWinnerSide({ kind, p1: draft.p1, p2: draft.p2, tb1: "", tb2: "" });
      if (side === 2) {
        tb1 = draft.tbLose;
        tb2 = draft.tbWin;
      } else {
        tb1 = draft.tbWin;
        tb2 = draft.tbLose;
      }
    }
    return { kind, p1: draft.p1, p2: draft.p2, tb1, tb2 };
  });
  return {
    winner: winner ?? null,
    resultType,
    sets: sets.length ? sets : [emptyCaptureSet(), emptyCaptureSet()],
    notes,
  };
}

function toDraft(set: CaptureSet, pickleball: boolean): ScoreSetDraft {
  const superTb = !pickleball && set.kind === "super";
  let tbWin = "";
  let tbLose = "";
  if (!superTb && !pickleball && (set.tb1.trim() || set.tb2.trim())) {
    const side = setWinnerSide(set);
    if (side === 2) {
      tbWin = set.tb2.trim();
      tbLose = set.tb1.trim();
    } else {
      tbWin = set.tb1.trim();
      tbLose = set.tb2.trim();
    }
  }
  return { p1: set.p1.trim(), p2: set.p2.trim(), tbWin, tbLose, superTb };
}

/** El texto que se manda (igual que la captura anterior). */
export function captureToScoreText(state: CaptureState, pickleball: boolean): string {
  const suffix = state.resultType === "default" ? " DEF" : state.resultType === "retiro" ? " RET" : "";
  return `${scoreTextFromDraft({ sets: state.sets.map((set) => toDraft(set, pickleball)) })}${suffix}`.trim();
}

/** Un set desde el lado de quien ganó el PARTIDO: «6-4», «6-7 (4-7)»,
 * «10-4». `winnerSide` 1 o 2. */
export function setChipFromWinner(set: CaptureSet, winnerSide: 1 | 2, pickleball: boolean): string {
  const mine = winnerSide === 1 ? set.p1 : set.p2;
  const theirs = winnerSide === 1 ? set.p2 : set.p1;
  let chip = `${mine.trim() || "0"}-${theirs.trim() || "0"}`;
  if (tiebreakApplies(set, pickleball) && (set.tb1.trim() || set.tb2.trim())) {
    const tbMine = winnerSide === 1 ? set.tb1 : set.tb2;
    const tbTheirs = winnerSide === 1 ? set.tb2 : set.tb1;
    chip += ` (${tbMine.trim() || "?"}-${tbTheirs.trim() || "?"})`;
  }
  return chip;
}

export type CaptureIssueKind = "super_short" | "super_diff" | "incomplete" | "empty" | "tied" | "tb_mismatch";

export interface CaptureSetIssue {
  kind: CaptureIssueKind;
  message: string;
  /** Arreglo de un toque: «Sí, cambiar a set normal». */
  fixToNormal?: boolean;
}

export function setLabel(index: number, _set: CaptureSet, pickleball: boolean): string {
  if (pickleball) return `Game ${index + 1}`;
  return `Set ${index + 1}`;
}

/** Avisos de UN set: solo lo imposible. */
export function setIssues(
  set: CaptureSet,
  index: number,
  players: [CapturePlayer, CapturePlayer],
  pickleball: boolean,
  resultType: CaptureResultType,
  isLast = true,
): CaptureSetIssue[] {
  const a = num(set.p1);
  const b = num(set.p2);
  const label = setLabel(index, set, pickleball);
  // Revisión 2026-10-06: en retiro/default solo el ÚLTIMO set puede quedar a
  // medias; los anteriores se jugaron completos y se revisan igual.
  const partialAllowed = resultType !== "normal" && isLast;
  if (a === null && b === null) {
    // Retiro/default pueden dejar sets sin jugar.
    if (resultType !== "normal") return [];
    return [{ kind: "empty", message: `Falta el marcador del ${label.toLowerCase()}. Llénalo o quítalo con «Quitar».` }];
  }
  if (a === null || b === null) {
    const missing = a === null ? players[0].name : players[1].name;
    return [{ kind: "incomplete", message: `Falta el marcador de ${missing} en el ${label.toLowerCase()}.` }];
  }
  if (set.kind === "super" && !pickleball) {
    if (partialAllowed) return [];
    const hi = Math.max(a, b);
    const lo = Math.min(a, b);
    if (hi < 10) {
      return [
        {
          kind: "super_short",
          message: `Una súper muerte se juega a 10 puntos, y aquí quedó ${a}-${b}. ¿Fue un set normal?`,
          fixToNormal: hi <= 7,
        },
      ];
    }
    if ((hi === 10 && hi - lo < 2) || (hi > 10 && hi - lo !== 2)) {
      return [{ kind: "super_diff", message: `La súper muerte se gana por 2 puntos de diferencia: ${a}-${b} no puede quedar así.` }];
    }
    return [];
  }
  if (a === b && !partialAllowed) {
    return [{ kind: "tied", message: `Un ${pickleball ? "game" : "set"} no puede quedar empatado (${a}-${b}).` }];
  }
  // Revisión 2026-10-06: el tie-break lo gana quien ganó el set.
  const t1 = num(set.tb1);
  const t2 = num(set.tb2);
  if (!pickleball && t1 !== null && t2 !== null && a !== b) {
    const setWinnerPts = a > b ? t1 : t2;
    const setLoserPts = a > b ? t2 : t1;
    if (setWinnerPts <= setLoserPts) {
      const winnerName = a > b ? players[0].name : players[1].name;
      return [
        {
          kind: "tb_mismatch",
          message: `El tie-break no cuadra con quien ganó el ${label.toLowerCase()}: ${winnerName} ganó el set, pero quedó ${t1}-${t2} en el tie-break.`,
        },
      ];
    }
  }
  return [];
}

export type CaptureBlock =
  | { kind: "ok" }
  | { kind: "set_issues" }
  | { kind: "empty_sets" }
  | { kind: "no_decider" }
  | { kind: "no_winner"; impliedSide: 1 | 2 | null }
  | { kind: "winner_mismatch"; impliedSide: 1 | 2 };

/** ¿Se puede enviar? Lo que falta, en orden de importancia. */
export function captureBlock(state: CaptureState, players: [CapturePlayer, CapturePlayer], pickleball: boolean): CaptureBlock {
  const issues = state.sets.flatMap((set, index) =>
    setIssues(set, index, players, pickleball, state.resultType, index === state.sets.length - 1),
  );
  if (issues.some((issue) => issue.kind !== "empty")) return { kind: "set_issues" };
  if (issues.length) return { kind: "empty_sets" };
  const implied = impliedWinnerSide(state.sets);
  if (state.winner !== null && implied !== null) {
    const chosenSide = state.winner === players[0].id ? 1 : 2;
    if (chosenSide !== implied) return { kind: "winner_mismatch", impliedSide: implied };
  }
  if (state.resultType === "normal") {
    const { p1, p2 } = setsTally(state.sets);
    if (p1 === p2) return { kind: "no_decider" };
  }
  if (state.winner === null) return { kind: "no_winner", impliedSide: implied };
  return { kind: "ok" };
}

/** ¿Mostrar la tarjeta «Van 1-1. ¿Cómo se definió?»? */
export function showDeciderCard(state: CaptureState, pickleball: boolean): boolean {
  if (state.sets.length !== 2 || state.sets.length >= maxSets(pickleball)) return false;
  const { p1, p2 } = setsTally(state.sets);
  return p1 === 1 && p2 === 1;
}


/** Avisos cortos de un reporte YA enviado, para la tabla del admin
 * («⚠ Set 1 marcado como súper muerte con 6-0»). Mismas reglas que la
 * captura: solo lo imposible. */
export function reportWarnings(
  scoreText: string | null | undefined,
  winner: number | null | undefined,
  players: [CapturePlayer, CapturePlayer],
  pickleball: boolean,
): string[] {
  const state = captureFromScoreText(scoreText, winner, pickleball);
  const out: string[] = [];
  state.sets.forEach((set, index) => {
    const label = setLabel(index, set, pickleball);
    for (const issue of setIssues(set, index, players, pickleball, state.resultType, index === state.sets.length - 1)) {
      if (issue.kind === "super_short" || issue.kind === "super_diff") {
        out.push(`${label} marcado como súper muerte con ${set.p1}-${set.p2}`);
      } else if (issue.kind === "incomplete") {
        out.push(`${label} incompleto`);
      } else if (issue.kind === "tb_mismatch") {
        out.push(`${label}: el tie-break no cuadra con quien ganó el set`);
      } else if (issue.kind === "tied") {
        out.push(`${label} empatado ${set.p1}-${set.p2}`);
      }
    }
  });
  const block = captureBlock(state, players, pickleball);
  if (block.kind === "winner_mismatch") out.push("El ganador no coincide con los sets");
  if (block.kind === "no_decider" && state.sets.some((set) => set.p1 || set.p2)) out.push("Van empatados en sets: falta el que define");
  return out;
}
